import type { Direction, SaveData } from "../domain/types";
import { GameEngine, type MoveResult } from "../game/GameEngine";
import { createLevel } from "../game/LevelSystem";
import { calculateRunReward } from "../domain/RewardService";
import { applyRelicFragments, type RelicDefinition } from "../domain/RelicService";
import { calculateStars, milestoneFragments } from "../domain/VerticalSlice";
import { checkAchievements, type AchievementDefinition } from "../domain/AchievementService";
import { dateKey } from "../domain/DailyChallenge";

type SavePort = { save(data: SaveData): void };

export class RunSession {
  engine?: GameEngine;
  lastUnlockedRelic?: RelicDefinition;
  lastStars = 0;
  lastMilestoneFragments = 0;
  newlyUnlockedAchievements: AchievementDefinition[] = [];
  /** Set by the caller before start() when this run is today's daily challenge. */
  dailyMode = false;

  constructor(public save: SaveData, private readonly saves: SavePort) {}

  replaceSave(save: SaveData): void {
    this.save = save;
    this.engine = undefined;
    this.lastUnlockedRelic = undefined;
    this.lastStars = 0;
    this.lastMilestoneFragments = 0;
    this.newlyUnlockedAchievements = [];
  }

  start(seed: number): GameEngine {
    this.lastUnlockedRelic = undefined;
    this.newlyUnlockedAchievements = [];
    this.engine = new GameEngine(createLevel(this.save.progression.campaignLevel, seed));
    return this.engine;
  }

  restart(seed: number): GameEngine {
    return this.start(seed);
  }

  move(direction: Direction): MoveResult {
    if (!this.engine) return { valid: false, message: "No expedition is active." };
    const result = this.engine.move(direction);
    if (result.event === "win" || result.event === "loss") this.persistResult(result.event === "win");
    return result;
  }

  tick(seconds = 1): MoveResult {
    if (!this.engine) return { valid: false, message: "No expedition is active." };
    const result = this.engine.tick(seconds);
    if (result.event === "loss") this.persistResult(false);
    return result;
  }

  persistResult(won: boolean, now: Date = new Date()): void {
    if (!this.engine) return;
    const state = this.engine.state;
    const reward = calculateRunReward(state, this.save.progression.campaignLevel, won);
    this.save.progression.xp += reward.xp;
    this.save.progression.runs++;
    this.save.progression.bestScore = Math.max(this.save.progression.bestScore, state.score);
    this.save.progression.totalTreasuresCollected += state.treasuresCollected;
    this.save.economy.gold += reward.gold;
    this.save.economy.gems += state.rewards.gems;
    this.save.economy.relics += state.rewards.relics;
    const relicResult = applyRelicFragments(
      { relicFragments: this.save.relicFragments, unlockedRelics: this.save.unlockedRelics },
      state.rewards.relicFragments,
    );
    this.save.relicFragments = relicResult.progress.relicFragments;
    this.save.unlockedRelics = relicResult.progress.unlockedRelics;
    this.lastUnlockedRelic = relicResult.unlocked;
    this.save.economy.masteryPoints += reward.masteryPoints;
    if (won) {
      this.lastStars = calculateStars(state, true);
      this.save.progression.levelStars[state.levelId] = Math.max(
        this.save.progression.levelStars[state.levelId] ?? 0, this.lastStars,
      );
      const completedLevel = this.save.progression.campaignLevel;
      this.lastMilestoneFragments = milestoneFragments(completedLevel, this.save.progression.milestoneRewardsClaimed);
      if (this.lastMilestoneFragments) {
        this.save.relicFragments += this.lastMilestoneFragments;
        this.save.progression.milestoneRewardsClaimed.push(completedLevel);
      }
      if (!this.save.progression.completedLevels.includes(state.levelId)) {
        this.save.progression.completedLevels.push(state.levelId);
      }
      this.save.progression.campaignLevel++;
    }
    if (won && this.dailyMode) {
      const today = dateKey(now);
      this.save.daily.lastPlayedDate = today;
      if (!this.save.daily.completedDates.includes(today)) this.save.daily.completedDates.push(today);
    }
    this.newlyUnlockedAchievements = checkAchievements({ save: this.save, state, won });
    if (this.newlyUnlockedAchievements.length) {
      const timestamp = now.toISOString();
      for (const achievement of this.newlyUnlockedAchievements) {
        this.save.achievements.push(achievement.id);
        this.save.achievementUnlockedAt[achievement.id] = timestamp;
      }
    }
    this.saves.save(this.save);
  }
}
